import { addProperty as backendAddProperty } from '@/backend/addProperty'
import {
  addEntity as backendAddEntity,
  recalculateEntitites as backendRecalculateEntities,
  recalculateEntity as backendRecalculateEntity,
} from '@/backend/entity'
import { getEffectivePlan } from '@/backend/getEffectivePlan'
import type { APIResult } from '@/backend/types'
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import { useLocalStorage } from '@vueuse/core'
import { PAYWALL_EVENT, usePaywallStore, type PaywallEvent } from './paywall'
import { serializePlan, useBilling } from './useBilling'

export const LIMIT_EXCEEDED_CODE = 'billing_limits_exceeded'

/**
 * Some objects have usage that is limited by the workspace's active plan. This
 * composable provides wrappers around the backend functions that create these
 * objects, so that we can show a paywall dialog if the user tries to perform an
 * action that would exceed their plan's limits.
 */
export const useLimitedAction = () => {
  const paywallStore = usePaywallStore()
  const billingStore = useBilling()
  const { captureAnalyticsEvent } = useAnalytics()

  /**
   * Middleware to handle 402 errors from the backend, which indicate that the
   * user's plan limits have been exceeded. If this happens, we open the paywall
   * dialog and update the active plan in the billing store.
   */
  const handleApiResponse = async (
    workspaceId: string,
    response: APIResult<unknown>,
    action: PaywallEvent,
  ) => {
    if (!response.ok && response.error.code === LIMIT_EXCEEDED_CODE) {
      paywallStore.open({ action })

      // Refresh the effective plan, as other users may have increased the limit usage
      // and we want to make sure that the current user sees the most up-to-date
      // usage state.
      const listEffectivePlansResponse = await getEffectivePlan(workspaceId)
      if (listEffectivePlansResponse.ok) {
        billingStore.activePlan = serializePlan(listEffectivePlansResponse.data)
      }
    }
  }

  const addEntity: typeof backendAddEntity = async (params) => {
    const response = await backendAddEntity(params)
    await handleApiResponse(params.workspaceId, response, PAYWALL_EVENT.ADD_ENTITY)
    // Composable usage in an action rather than top-level so that we avoid calling it too many times,
    // such as for every cell in a project table.
    // eslint-disable-next-line internal/no-call-composable-outside-setup
    const timesAdded = useLocalStorage('times-added-prop', 0)
    if (response.ok) {
      timesAdded.value++
    }
    if (timesAdded.value === 5) {
      captureAnalyticsEvent(ANALYTICS_EVENT.FIVE_ENTITIES_CREATED)
    }
    return response
  }

  const addProperty: typeof backendAddProperty = async (params) => {
    const response = await backendAddProperty(params)
    await handleApiResponse(params.workspaceId, response, PAYWALL_EVENT.ADD_PROPERTY)
    return response
  }

  const recalculateEntities: typeof backendRecalculateEntities = async (params) => {
    const response = await backendRecalculateEntities(params)
    await handleApiResponse(params.workspaceId, response, PAYWALL_EVENT.RECALCULATE_ENTITIES)
    return response
  }

  const recalculateEntity: typeof backendRecalculateEntity = async (params) => {
    const response = await backendRecalculateEntity(params)
    await handleApiResponse(params.workspaceId, response, PAYWALL_EVENT.RECALCULATE_ENTITIES)
    return response
  }

  return {
    /**
     * Provides a wrapper around the backend's addEntity function that shows a
     * paywall dialog if the user's plan limits have been exceeded.
     */
    addEntity,
    /**
     * Provides a wrapper around the backend's addProperty function that shows a
     * paywall dialog if the user's plan limits have been exceeded.
     */
    addProperty,
    /**
     * Provides a wrapper around the backend's recalculateEntities function
     * that shows a paywall dialog if the user's plan limits have been exceeded.
     */
    recalculateEntities,
    /**
     * Provides a wrapper around the backend's recalculateEntity function that
     * shows a paywall dialog if the user's plan limits have been exceeded.
     */
    recalculateEntity,
  }
}
